// frontend/src/features/categories/categoriesApiSlice.js
import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const categoriesAdapter = createEntityAdapter({
    sortComparer: (a, b) => a.name.localeCompare(b.name)
});

const initialState = categoriesAdapter.getInitialState();

export const categoriesApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getCategoriesPublic: builder.query({
            query: ({ page = 1, search = '' }) => {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                if (search) params.append('search', search);
                return {
                    url: `/api/v1/categories?${params.toString()}`,
                    method: 'GET',
                };
            },
            transformResponse: responseData => {
                const data = responseData.categories || [];
                const loadedCategories = data.map(category => {
                    category.id = category._id;
                    return category;
                });
                return categoriesAdapter.setAll(initialState, loadedCategories);
            },
            providesTags: (result) =>
                result?.ids
                    ? [
                        { type: 'Category', id: 'LIST_PUBLIC' },
                        ...result.ids.map(id => ({ type: 'Category', id }))
                    ]
                    : [{ type: 'Category', id: 'LIST_PUBLIC' }]
        }),

        getAllCategoriesAdmin: builder.query({
            query: ({ page = 1, search = '', isActive = '' }) => {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                if (search) params.append('keyword', search);
                if (isActive !== '') params.append('isActive', isActive);
                return {
                    url: `/api/v1/admin/categories?${params.toString()}`,
                    method: 'GET',
                };
            },
            transformResponse: responseData => {
                const data = responseData.categories || [];
                const loadedCategories = data.map(category => {
                    return {
                        id: category._id,
                        ...category
                    };
                });
                return {
                    ...categoriesAdapter.setAll(initialState, loadedCategories),
                    totalPages: responseData.totalPages,
                    currentPage: responseData.currentPage,
                    filteredCategoriesCount: responseData.filteredCategoriesCount,
                };
            },
            providesTags: (result) =>
                result?.ids
                    ? [
                        { type: 'Category', id: 'LIST_ADMIN' },
                        ...result.ids.map(id => ({ type: 'Category', id }))
                    ]
                    : [{ type: 'Category', id: 'LIST_ADMIN' }]
        }),

        addNewCategory: builder.mutation({
            query: formData => ({
                url: '/api/v1/admin/categories',
                method: 'POST',
                body: formData,
            }),
            invalidatesTags: [
                { type: 'Category', id: "LIST_ADMIN" },
                { type: 'Category', id: "LIST_PUBLIC" }
            ]
        }),
        updateCategory: builder.mutation({
            query: ({ id, ...patch }) => ({
                url: `/api/v1/admin/categories/${id}`,
                method: 'PUT',
                body: patch,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Category', id: arg.id },
                { type: 'Category', id: 'LIST_ADMIN' },
                { type: 'Category', id: 'LIST_PUBLIC' }
            ]
        }),
        deleteCategory: builder.mutation({
            query: ({ id }) => ({
                url: `/api/v1/admin/categories/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Category', id: arg.id },
                { type: 'Category', id: 'LIST_ADMIN' },
                { type: 'Category', id: 'LIST_PUBLIC' }
            ]
        }),

        // ****** NEW MUTATION FOR IMAGE UPLOAD ******
        uploadCategoryImage: builder.mutation({
            query: (imageData) => ({
                url: '/api/v1/admin/upload-category-image', // <--- **यह URL आपके बैकएंड के अनुसार होना चाहिए**
                method: 'POST',
                body: imageData, // Expects FormData
            }),
            // If the upload mutation itself doesn't directly affect category list, no invalidatesTags needed here.
            // The imageUrl from this mutation will be used in updateCategory.
        }),
        // ********************************************
    }),
});

export const {
    useGetCategoriesPublicQuery,
    useGetAllCategoriesAdminQuery,
    useAddNewCategoryMutation,
    useUpdateCategoryMutation,
    useDeleteCategoryMutation,
    useUploadCategoryImageMutation, // <-- NEW EXPORT
} = categoriesApiSlice;

// ***************************************************************
// ** THESE ARE THE CRUCIAL, CORRECTED SELECTORS FOR ADMIN VIEW **
// ***************************************************************

export const selectCategoriesResultByArgs = createSelector(
    (state, queryArgs) => categoriesApiSlice.endpoints.getAllCategoriesAdmin.select(queryArgs)(state),
    categoriesResult => {
        const data = categoriesResult?.data ?? {
            ...categoriesAdapter.getInitialState(),
            totalPages: 0,
            currentPage: 1,
            filteredCategoriesCount: 0
        };
        return data;
    }
);

export const selectCategoryByIdForAdmin = createSelector(
    (state, queryArgs) => selectCategoriesResultByArgs(state, queryArgs),
    (state, queryArgs, categoryId) => categoryId,
    (categoriesData, categoryId) => categoriesAdapter.getSelectors().selectById(categoriesData, categoryId)
);

export const selectAllCategoriesForAdminByArgs = createSelector(
    (state, queryArgs) => selectCategoriesResultByArgs(state, queryArgs),
    (categoriesData) => categoriesAdapter.getSelectors().selectAll(categoriesData)
);

// ***************************************************************
// ** PUBLIC CATEGORIES SELECTORS (No changes, keep as is)      **
// ***************************************************************
export const selectCategoriesPublicResult = categoriesApiSlice.endpoints.getCategoriesPublic.select();

const selectPublicCategoriesData = createSelector(
    selectCategoriesPublicResult,
    categoriesResult => categoriesResult.data ?? categoriesAdapter.getInitialState()
);

const selectPublicCategoriesNormalizedState = createSelector(
    selectPublicCategoriesData,
    (data) => {
        return data ? { ids: data.ids, entities: data.entities } : categoriesAdapter.getInitialState();
    }
);

export const {
    selectAll: selectAllCategoriesForPublic,
    selectById: selectCategoryByIdForPublic,
    selectIds: selectCategoryIdsForPublic,
} = categoriesAdapter.getSelectors(state => selectPublicCategoriesNormalizedState(state));